import { IonIcon } from "@ionic/react";
import { searchOutline } from "ionicons/icons";
import { useCallback, useContext, useEffect, useState } from "react";
import { useOutletContext } from "react-router";
import styled from "styled-components";
import { loadThemePrices } from "../../../../services/EncarteThemeService.js";
import Button from "../../../sharable/ActionButton.jsx";
import EncarteCard from "../../../sharable/EncarteCard.jsx";
import { Input } from "../../../sharable/InputStyle.jsx";
import ThemeListSlider from "../../../sharable/ThemeListSlider/ThemeListSlider.jsx";
import WhatsAppAnimatedLink from "../../../sharable/WhatsAppAnimatedLink.jsx";
import WorkspaceContext from "../../WorkspaceContext.js";
import { MenuDescription, MenuTitle } from "../Toolbar.jsx";
import useFetchAvailableToPlanThemes from "./hooks/useFetchAvailableToPlanThemes.js";
import useFetchHolidayThemes from "./hooks/useFetchHolidayThemes.js";
import useFetchLatestThemes from "./hooks/useFetchLatestThemes.js";
import useFetchThemesByDaysPeriod from "./hooks/useFetchThemesByDaysPeriod.js";
import useFetchThemesByName from "./hooks/useFetchThemesByName.js";
import useFetchTodayThemes from "./hooks/useFetchTodayThemes.js";
import useFetchUserThemes from "./hooks/useFetchUserThemes.js";

const Container = styled.div`
  height: calc(100vh - 8.4rem);
  overflow-y: auto;
  overflow-x: hidden;

  .model-name {
    width: 100%;
    margin-bottom: 1.4rem;
  }

  .load-button-wrapper {
    position: relative;
  }
`;
const CategoriesContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 2rem;
`;

const Category = styled.div`
  padding: 0.6rem 0.8rem;
  font-size: 1.6rem;
  background-color: #0a0a0a;
  margin-right: 0;
  border-top-right-radius: 10px;
  border-top-left-radius: 10px;
`;

const CategoryEncarteWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  position: relative;
`;

const EncarteContainer = styled.div`
  display: grid;
  grid-template-columns: min-content min-content min-content;
  position: relative;
  gap: 2rem;
  justify-content: center;
  margin-top: 1rem;
`;

const NoModelFoundSpan = styled.span`
  display: block;
  position: absolute;
  left: 0;
  right: 0;
  font-size: 1.4rem;
  text-align: center;
`;

const ResultsCategory = styled(Category)`
  position: relative;
  display: flex;
  align-items: center;
  padding-left: 3rem;
  margin-top: 2rem;
`;

export default function ModelsMenuView() {
  const {
    onEncarteThemeChange,
    onEncarteThemesSearchResultChange,
    encarteThemesSearchResult,
  } = useContext(WorkspaceContext);
  const [themeNameInput, setThemeNameInput] = useState("");
  const { token, isAccountPremium, accountStatus, user } = useOutletContext();

  const fetchLatestThemes = useCallback(useFetchLatestThemes(), []);
  const fetchThemesByName = useCallback(useFetchThemesByName(), []);
  const fetchAvailableToPlanThemes = useCallback(
    useFetchAvailableToPlanThemes(),
    [],
  );
  const fetchHolidayThemes = useCallback(useFetchHolidayThemes(), []);
  const fetchTodayThemes = useCallback(useFetchTodayThemes(), []);
  const fetchThemesByDaysPeriod = useCallback(useFetchThemesByDaysPeriod(), []);
  const fetchUserThemes = useCallback(useFetchUserThemes(), []);

  useEffect(() => {
    if (themeNameInput?.length === 0) {
      onEncarteThemesSearchResultChange(null);
    }
  }, [themeNameInput]);

  const handleThemeClick = async (t) => {
    t = await loadThemePrices(t, token);
    onEncarteThemeChange(t);
  };

  const onThemeNameSearch = async (themeNameInput) => {
    const results = await fetchThemesByName(token, themeNameInput, 0, 9);
    onEncarteThemesSearchResultChange(results);
  };

  const getTitleBasedOnDay = () => {
    const currentDay = new Date().getDate();

    let title = "";
    if (currentDay <= 7) title = "Abra o mês com tudo!";
    else if (currentDay <= 15) title = "Quinzena!";
    else if (currentDay > 22) title = "Feche o mês vendendo muito!";
    else title = "Final de mês: Última chance para seus encartes!";

    return title;
  };

  return (
    <Container>
      <MenuDescription>
        Escolha um tema para começar a criar seu encarte. Você pode pesquisar
        temas por nome ou filtrar por categorias e cores.
      </MenuDescription>
      <div className="flex space-between align-center">
        <MenuTitle>Temas</MenuTitle>
        <WhatsAppAnimatedLink
          email={user.email}
          messageContent={`Gostaria de solicitar o tema: ${themeNameInput}`}
        >
          Solicitar novo tema
        </WhatsAppAnimatedLink>
      </div>
      <Input
        value={themeNameInput}
        onInput={(e) => setThemeNameInput(e.target.value)}
        className="model-name"
        placeholder="Nome do tema que deseja buscar..."
      />
      {themeNameInput && themeNameInput.length >= 3 && (
        <Button
          text="Buscar tema"
          color="#fff"
          $fullWidth={true}
          style={{ marginBottom: "2rem" }}
          action={async () => {
            if (themeNameInput?.length >= 3) {
              await onThemeNameSearch(themeNameInput);
            }
          }}
          icon={<IonIcon icon={searchOutline} />}
        />
      )}
      {themeNameInput && themeNameInput.length < 3 && (
        <span
          style={{ fontSize: "1.2rem", color: "#888", marginBottom: "1rem" }}
        >
          Digite ao menos 3 caracteres para buscar um tema.
        </span>
      )}

      <div style={{ textAlign: "center" }}></div>

      <CategoriesContainer>
        <CategoryEncarteWrapper>
          {encarteThemesSearchResult?.resources?.length > 0 && (
            <ResultsCategory>
              <span> Resultados encontrados</span> <div className="icon"></div>
            </ResultsCategory>
          )}
          {
            <EncarteContainer>
              {encarteThemesSearchResult?.resources?.length > 0 &&
                encarteThemesSearchResult.resources.map((m) => {
                  return (
                    <EncarteCard
                      onClick={() => handleThemeClick(m)}
                      url={m.urlTmp}
                      name={m.name.split(".")[0]}
                      isIncludedInPlan={
                        isAccountPremium || !m.isPremiumResource
                      }
                      showPremiumTag={
                        (!isAccountPremium && m.isPremiumResource) ||
                        (m.isPremiumResource && accountStatus.onTrial)
                      }
                    />
                  );
                })}
              {themeNameInput &&
                encarteThemesSearchResult?.resources?.length === 0 && (
                  <NoModelFoundSpan>Nenhum tema encontrado.</NoModelFoundSpan>
                )}
            </EncarteContainer>
          }
        </CategoryEncarteWrapper>
        <CategoryEncarteWrapper>
          <ThemeListSlider
            accountStatus={accountStatus}
            isAccountPremium={isAccountPremium}
            onThemeClick={handleThemeClick}
            title={"Exclusivos para você!"}
            fetchThemes={fetchUserThemes}
            showWhenEmpty={true}
            emptyStateText={
              "Você ainda não tem temas personalizados. Fale com a gente e solicite o seu agora."
            }
            emptyStateCta={
              <WhatsAppAnimatedLink
                email={user.email}
                messageContent={"Gostaria de solicitar um tema personalizado."}
              >
                Solicitar tema personalizado
              </WhatsAppAnimatedLink>
            }
          />
          <ThemeListSlider
            accountStatus={accountStatus}
            isAccountPremium={isAccountPremium}
            onThemeClick={handleThemeClick}
            title={"Novidades!"}
            fetchThemes={fetchLatestThemes}
            titleAnimation={true}
          />
          {!accountStatus.isAccountPremium && (
            <ThemeListSlider
              accountStatus={accountStatus}
              isAccountPremium={isAccountPremium}
              onThemeClick={handleThemeClick}
              title={"Disponíveis para você"}
              fetchThemes={fetchAvailableToPlanThemes}
            />
          )}
          <ThemeListSlider
            accountStatus={accountStatus}
            isAccountPremium={isAccountPremium}
            onThemeClick={handleThemeClick}
            title={"Crie hoje!"}
            fetchThemes={fetchTodayThemes}
          />

          <ThemeListSlider
            accountStatus={accountStatus}
            isAccountPremium={isAccountPremium}
            onThemeClick={handleThemeClick}
            title={"Recomendados"}
            fetchThemes={fetchHolidayThemes}
          />
          <ThemeListSlider
            accountStatus={accountStatus}
            isAccountPremium={isAccountPremium}
            onThemeClick={handleThemeClick}
            title={getTitleBasedOnDay()}
            fetchThemes={fetchThemesByDaysPeriod}
          />
        </CategoryEncarteWrapper>
      </CategoriesContainer>
    </Container>
  );
}
